import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Minus, Plus } from "lucide-react";

const labelCls = "mb-1 block text-xs font-semibold text-ink";
const inputCls = "w-full rounded-lg border border-sand bg-white px-3.5 py-2.5 text-sm outline-none focus:border-terra";

const Stepper = ({ label, value, set, tid }) => (
  <div className="flex items-center justify-between rounded-lg border border-sand px-3.5 py-2">
    <span className="text-xs font-semibold text-ink">{label}</span>
    <span className="flex items-center gap-2.5">
      <button type="button" onClick={() => set(Math.max(0, value - 1))} data-testid={`${tid}-minus`} className="flex h-6 w-6 items-center justify-center rounded-full border border-sand text-ink hover:border-terra"><Minus className="h-3 w-3" /></button>
      <span className="w-4 text-center font-mono text-sm font-bold" data-testid={`${tid}-value`}>{value}</span>
      <button type="button" onClick={() => set(Math.min(9, value + 1))} data-testid={`${tid}-plus`} className="flex h-6 w-6 items-center justify-center rounded-full border border-sand text-ink hover:border-terra"><Plus className="h-3 w-3" /></button>
    </span>
  </div>
);

export default function SimCustomerInquiry() {
  const [stage, setStage] = useState("package");
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(1);
  const [name, setName] = useState("Rahul Sharma");
  const [sent, setSent] = useState(false);

  return (
    <div className="mx-auto max-w-xl" data-testid="sim-customer-inquiry">
      <AnimatePresence mode="wait">
        {stage === "package" && (
          <motion.div key="pkg" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="overflow-hidden rounded-2xl border border-sand bg-white">
            <div className="relative h-44">
              <img src="https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=1000&q=70" alt="Maldives Escape" loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/65 to-transparent p-5">
                <p className="font-serif text-2xl text-white">Maldives Escape</p>
                <p className="text-xs text-white/80">6 Nights / 7 Days · Water Villa · Private Transfers</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 p-5">
              <div>
                <p className="text-[11px] text-stone2">Gallery · Itinerary · Hotels · Inclusions · Terms</p>
                <p className="mt-1 text-sm text-stone2">Starting from <span className="font-serif text-xl text-ink">$1,499</span></p>
              </div>
              <button onClick={() => setStage("form")} data-testid="sim-inquiry-start-btn"
                className="group inline-flex items-center gap-2 rounded-full bg-ink px-6 py-3 text-sm font-semibold text-paper transition-colors duration-300 hover:bg-terra hover:text-ink">
                Plan This Trip <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </motion.div>
        )}

        {stage === "form" && (
          <motion.div key="form" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="rounded-2xl border border-sand bg-white p-5 sm:p-6">
            <p className="mb-4 flex items-center justify-between font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-stone2">
              Request this trip <span className="rounded-full bg-parch px-2 py-0.5 text-[9px] normal-case tracking-normal">simulation</span>
            </p>
            <div className="grid grid-cols-2 gap-2.5">
              <div><label className={labelCls}>From</label><input type="date" defaultValue="2027-02-12" data-testid="sim-inquiry-date-from" className={inputCls} /></div>
              <div><label className={labelCls}>To</label><input type="date" defaultValue="2027-02-18" data-testid="sim-inquiry-date-to" className={inputCls} /></div>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              <Stepper label="Adults" value={adults} set={setAdults} tid="sim-inquiry-adults" />
              <Stepper label="Children" value={children} set={setChildren} tid="sim-inquiry-children" />
            </div>
            <div className="mt-2.5 grid grid-cols-3 gap-2.5">
              <div><label className={labelCls}>Hotel</label><select data-testid="sim-inquiry-hotel" className={inputCls}><option>5 Star</option><option>4 Star</option><option>Boutique</option></select></div>
              <div><label className={labelCls}>Meal</label><select data-testid="sim-inquiry-meal" className={inputCls}><option>Half Board</option><option>Breakfast</option><option>All Inclusive</option></select></div>
              <div><label className={labelCls}>Transfer</label><select data-testid="sim-inquiry-transfer" className={inputCls}><option>Private</option><option>Shared</option></select></div>
            </div>
            <div className="mt-2.5 grid grid-cols-2 gap-2.5">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" data-testid="sim-inquiry-name" className={inputCls} />
              <input placeholder="WhatsApp" data-testid="sim-inquiry-whatsapp" className={inputCls} />
            </div>
            <textarea placeholder="Anything else we should know? (water villa, honeymoon setup…)" rows={2} data-testid="sim-inquiry-notes"
              className={`${inputCls} mt-2.5 resize-none`} />
            <button onClick={() => { setStage("success"); setSent(false); setTimeout(() => setSent(true), 1800); }} data-testid="sim-inquiry-send-btn"
              className="mt-4 w-full rounded-full bg-terra py-3.5 text-sm font-bold text-ink transition-colors duration-300 hover:bg-ink hover:text-paper">
              Send Inquiry
            </button>
          </motion.div>
        )}

        {stage === "success" && (
          <motion.div key="success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-sand bg-white p-8 text-center" data-testid="sim-inquiry-success">
            <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.15 }}
              className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald2/10 text-emerald2">
              <Check className="h-7 w-7" />
            </motion.span>
            <p className="mt-4 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-emerald2">Inquiry received</p>
            <p className="mt-2 font-serif text-2xl text-ink">Thank you, {name.split(" ")[0] || "traveler"}.</p>
            <p className="mt-1 text-sm text-stone2">Your inquiry number:</p>
            <p className="mt-1 font-mono text-lg font-bold tracking-wider text-ink" data-testid="sim-inquiry-number">INQ-2027-00482</p>
            <p className="mx-auto mt-3 max-w-xs text-xs leading-relaxed text-stone2">
              Our travel specialist will review your requirements and get back to you shortly.
            </p>
            <div className="mx-auto mt-5 max-w-xs">
              <div className="h-1 overflow-hidden rounded-full bg-parch">
                <motion.div initial={{ x: "-100%" }} animate={{ x: sent ? "0%" : "-100%" }} transition={{ duration: 1.6, ease: "easeInOut" }}
                  className="h-full w-full rounded-full bg-terra" />
              </div>
              <p className={`mt-2 text-[11px] font-semibold transition-colors duration-500 ${sent ? "text-emerald2" : "text-stone2"}`} data-testid="sim-inquiry-delivered">
                {sent ? "✓ Delivered to the DMC's dashboard — see Step 06 below" : "Travelling to the DMC's dashboard…"}
              </p>
            </div>
            <button onClick={() => setStage("package")} data-testid="sim-inquiry-replay-btn" className="mt-4 text-xs font-bold text-terra hover:text-ink">
              Replay the customer flow
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
